import React, { useState } from 'react';
import { toast } from '../../../../../../ui/toast';

import { Button, Drawer, ListItemButton } from '../../../../../../ui';
import { addQuestionToAssessment } from '../../../../../../utils/api_service/assessment_api';
import {
  Question,
  QuestionSearch,
  SubSkill,
} from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { Filter } from './Components';
import { useSelectQuestions } from './Hooks';

interface ISelectQuestions {
  isOpen: boolean;
  onCloseDrawer: () => void;
  onSave: (finalSelection: Question[]) => void;
  assessmentId: string;
  skillList: SelectOptions[] | undefined;
  levelList: SelectOptions[] | undefined;
  subSkillList: SubSkill[] | undefined;
  jobTitleList: SelectOptions[] | undefined;
  initialSelected: Question[];
  onAddQuestion: (q: Question) => void;
}

export const SelectQuestions: React.FC<ISelectQuestions> = ({
  isOpen,
  onCloseDrawer,
  onSave,
  assessmentId,
  skillList,
  levelList,
  subSkillList,
  jobTitleList,
  initialSelected,
  onAddQuestion,
}) => {
  const [searchObject, setSearchObject] = useState<QuestionSearch>({
    questionTitle: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    jobTitle: '',
    assessmentId,
  });

  const {
    questionsList,
    selectedQuestions,
    loading,
    totalCount,
    pageNumber,
    totalPages,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    goToNextPage,
    goToPrevPage,
    setIsSaving,
    isSaving,
  } = useSelectQuestions({ searchObject, initialSelected });

  const handleSaveClick = async () => {
    setIsSaving(true);

    // Find only the newly-selected ones:
    const toAdd = selectedQuestions.filter(
      (q) => !initialSelected.some((s) => s.id === q.id),
    );

    try {
      await Promise.all(
        toAdd.map((q) =>
          addQuestionToAssessment(
            /* your assessmentQuestionId */ '',
            q.id,
            assessmentId,
          ),
        ),
      );
      toast.success('Questions added successfully!');
      // Now notify parent of the final array and close
      onSave(selectedQuestions);
    } catch (err) {
      toast.error('Failed to add some questions.');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        width='w-full md:w-[800px]'
        title='Invite Questions'
        footerContent={
          <div className='flex justify-end gap-5 w-full'>
            <Button label='Cancel' onClick={onCloseDrawer} variant='secondary' />
            <Button
                label={isSaving ? 'Saving' : 'Save'}
                onClick={handleSaveClick}
                variant='primary'
            />
          </div>
        }
    >
      <div className='flex gap-5 h-[80vh]'>
        { /* Filter pane */ }
        <div className='w-[250px]'>
          <Filter
              skillList={skillList || []}
              levelList={levelList || []}
              subSkillList={subSkillList}
              jobTitleList={jobTitleList}
              searchObject={searchObject}
              setSearchObject={setSearchObject}
          />
        </div>

        { /* Divider */ }
        <div className='border border-dashed border-gray-300 mx-2' />

        { /* Main content: top and bottom halves */ }
        <div className='flex flex-col flex-1 h-full'>
          { /* Top half: Available Questions */ }
          <div className='h-1/2 flex flex-col'>
            <div className='flex justify-between items-center mb-2'>
              <h3 className='font-semibold text-base'>All Questions</h3>
              <Button
                  variant='link'
                  label='Select All'
                  onClick={() => {
                    handleSelectAll();
                    questionsList.forEach(onAddQuestion);
                  }}
                  disabled={questionsList.length === 0}
              />
            </div>
            <div className='flex justify-between items-center mb-2'>
              <small>
                Page { pageNumber } of { totalPages } ({ totalCount } total)
              </small>
              <div className='flex gap-2'>
                <Button
                    label='Previous'
                    onClick={goToPrevPage}
                    variant='secondary'
                    disabled={pageNumber === 1 || loading}
                />
                <Button
                    label='Next'
                    onClick={goToNextPage}
                    variant='secondary'
                    disabled={pageNumber === totalPages || loading}
                />
              </div>
            </div>
            <div className='flex-1 overflow-y-auto border p-2 rounded'>
              { loading ? (
                <p className='text-sm text-gray-500'>Loading…</p>
              ) : questionsList.length === 0 ? (
                <p className='text-sm text-gray-500'>No questions available</p>
              ) : (
                <ul className='space-y-2'>
                  { questionsList.map((q) => (
                    <ListItemButton
                        key={q.id}
                        onClick={() => {
                          handleSelect(q);
                          onAddQuestion(q);
                        }}
                    >
                      { q.question || 'Untitled Question' }
                    </ListItemButton>
                  )) }
                </ul>
              ) }
            </div>
          </div>

          { /* Divider line */ }
          <div className='border-t border-gray-300 my-2' />

          { /* Bottom half: Selected Questions */ }
          <div className='h-1/2 flex flex-col'>
            <div className='flex justify-between items-center mb-2'>
              <h3 className='font-semibold text-base'>Selected Questions</h3>
              <Button
                  variant='link'
                  label='Remove All'
                  onClick={handleRemoveAll}
                  disabled={selectedQuestions.length === 0}
              />
            </div>
            <ul className='flex-1 overflow-y-auto border p-2 rounded space-y-2'>
              { selectedQuestions.length === 0 ? (
                <li className='text-sm text-gray-500'>No questions selected</li>
              ) : (
                selectedQuestions.map((q) => (
                  <ListItemButton
                      key={q.id}
                      isSelected
                      onClick={() => {}}
                      onRemove={() => handleRemove(q)}
                  >
                    { q.question || 'Untitled Question' }
                  </ListItemButton>
                ))
              ) }
            </ul>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SelectQuestions;
